import { useEffect, useState } from "react";
import { ArrowLeftIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { Button } from "./catalyst/button";
import { ProfileForm } from "./profile/ProfileFields";
import { useNavigate } from "react-router-dom";
import DiscardChangesDialog from "./dialogs/DiscardChangesDialog";
import TimePicker from "./TimePickerComponent";


const steps = ["Name", "Other details", "Review & save"];

type PrevRow = {
  name: string;
  from: string;
  to: string;
  type: "birth" | "specific" | "";
};

export type ProfileFormState = {
  givenName: string;
  middleNames: string;
  familyName: string;
  hadOtherLegalNames: "yes" | "no" | "";
  previousNames: PrevRow[];
  nicknames: string;
  pronoun: string;
  dob: string;
  tob: string;
  dominantHand: "" | "right" | "left" | "ambidextrous";
  placeOfBirth: string;
  currentResidence: string;
  spiritualOrientation: string;
  relationship?: string;
};

export type ProfileFormComponentProps = {
  initialData?: ProfileForm | null;
  onSave: (form: any) => Promise<void>;
  isRelated: boolean;
  isSetupProfile?:boolean
};

export default function ProfileFormComponent({
  initialData,
  onSave,
  isRelated,
  isSetupProfile= false
}: ProfileFormComponentProps) {
  // ---------- Helpers for sanitization & dates ----------
  const alphaOnly = (s: string, max = 50) => {
    // Allow letters and spaces only, remove leading spaces, enforce max
    const cleaned = s.replace(/[^A-Za-z\s]/g, "");
    const noLeading = cleaned.replace(/^\s+/, "");
    return noLeading.slice(0, max);
  };

  const nicknamesSanitize = (s: string, max = 50) => {
    // Allow letters, commas and spaces
    const cleaned = s.replace(/[^A-Za-z,\s]/g, "");
    const noLeading = cleaned.replace(/^\s+/, "");
    return noLeading.slice(0, max);
  };

  const placeSanitize = (s: string, max = 100) => {
    // Trim leading spaces, enforce max
    const noLeading = s.replace(/^\s+/, "");
    return noLeading.slice(0, max);
  };

  const stripNonAlpha = (s: string) => s.replace(/[^A-Za-z\s]/g, "").trim();

  const getYesterdayISO = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const nextDayISO = (isoDate: string) => {
    if (!isoDate) return "";
    const d = new Date(`${isoDate}T00:00:00`);
    d.setDate(d.getDate()); // min inclusive same day; tests asked earlier date greyed out => we'll use min = from
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const yesterday = getYesterdayISO();
  const navigate = useNavigate()

  // ---------- Defensive mapper (mapInitialData) ----------
  function mapInitialData(data?: ProfileForm | null): ProfileFormState {
    console.log("init Datata??????????????????/", data)
    const anyData = data as any;

    // Names
    let givenName = "";
    let middleNames = "";
    let familyName = "";

    if (typeof anyData?.fullName === "string" && anyData.fullName.trim()) {
      const parts = anyData.fullName.trim().split(/\s+/);
      givenName = parts[0] || "";
      familyName = parts.length > 1 ? parts[parts.length - 1] : "";
      middleNames = parts.length > 2 ? parts.slice(1, -1).join(" ") : "";
    } else if (anyData?.identity) {
      givenName = anyData.identity?.givenName || "";
      middleNames = anyData.identity?.middleNames || "";
      familyName = anyData.identity?.familyName || "";
    } else {
      givenName = anyData?.givenName || "";
      middleNames = anyData?.middleNames || "";
      familyName = anyData?.familyName || "";
    }

    // hadOtherLegalNames: convert boolean or "yes"/"no"
    let hadOther: "yes" | "no" | "" = "no";
    if (typeof anyData?.hadOtherLegalNames === "string") {
      hadOther = (anyData.hadOtherLegalNames as "yes" | "no") || "no";
    } else if (typeof anyData?.legalHistory?.hadOtherLegalNames === "boolean") {
      hadOther = anyData.legalHistory.hadOtherLegalNames ? "yes" : "no";
    } else if (anyData?.hadOtherLegalNames === true) {
      hadOther = "yes";
    } else {
      hadOther = anyData?.hadOtherLegalNames ?? "no";
    }

    // previous names
    const srcPrev = anyData?.legalHistory?.previousNames || anyData?.previousNames || [];
    const previousNames: PrevRow[] = (srcPrev || []).map((p: any) => ({
      name: p?.name || "",
      from: p?.from || p?.fromDate || "",
      to: p?.to || p?.toDate || "",
      type: (p?.type as "birth" | "specific") || "",
    }));

    // nicknames (array or string)
    let nicknamesStr = "";
    if (Array.isArray(anyData?.identity?.nicknames)) {
      nicknamesStr = (anyData.identity?.nicknames as string[]).join(", ");
    } else if (Array.isArray(anyData?.nicknames)) {
      nicknamesStr = (anyData.nicknames as string[]).join(", ");
    } else {
      nicknamesStr = anyData?.identity?.nicknames || anyData?.nicknames || "";
    }

    const pronoun = anyData?.identity?.pronoun || anyData?.pronoun || "";
    const dob = anyData?.birth?.date || anyData?.dob || "";
    const tob = anyData?.birth?.time || anyData?.tob || "";
    const dominantHand =
      (anyData?.traits?.dominantHand || anyData?.dominantHand || "right") as
        | "right"
        | "left"
        | "ambidextrous"
        | "";
    const placeOfBirth = anyData?.birth?.place || anyData?.placeOfBirth || "";
    const currentResidence = anyData?.current?.residence || anyData?.currentResidence || "";
    const spiritualOrientation = anyData?.traits?.spiritualOrientation || anyData?.spiritualOrientation || "";
    const relationship = anyData?.traits?.relationship || anyData?.relation || "";

    return {
      givenName: givenName || "",
      middleNames: middleNames || "",
      familyName: familyName || "",
      hadOtherLegalNames: hadOther,
      previousNames: previousNames.length ? previousNames : [],
      nicknames: nicknamesStr || "",
      pronoun,
      dob,
      tob,
      dominantHand: (dominantHand as "" | "right" | "left" | "ambidextrous") || "right",
      placeOfBirth,
      currentResidence,
      spiritualOrientation,
      relationship,
    };
  }

  // ---------- State ----------
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<ProfileFormState>(mapInitialData(initialData));
  const [loading, setLoading] = useState(false);
  const [discardOpen, setDiscardOpen]= useState<boolean>(false)

  // Inline error flags for place/residence/spiritual (min 3)
  const [errors, setErrors] = useState({
    placeOfBirthMin: false,
    currentResidenceMin: false,
    spiritualOrientationMin: false,
  });

  useEffect(() => {
    if (initialData != null) {
      setForm(mapInitialData(initialData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // ---------- Input handlers (sanitizing as user types) ----------
  const updateGeneric =
  (field: keyof ProfileFormState) =>
  (valueOrEvent: string | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    
    const value =
      typeof valueOrEvent === "string"
        ? valueOrEvent
        : valueOrEvent.target.value;

    setForm((f) => ({
      ...f,
      [field]: value,
    }));
  };


  const handleAlphaField =
    (field: "givenName" | "middleNames" | "familyName") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = alphaOnly(e.target.value, 50);
      setForm((f) => ({ ...f, [field]: v }));
    };

  const handleNicknamesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = nicknamesSanitize(e.target.value, 50);
    setForm((f) => ({ ...f, nicknames: v }));
  };

  const handlePlaceChange = (field: "placeOfBirth" | "currentResidence" | "spiritualOrientation") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = placeSanitize(e.target.value, 100);
    setForm((f) => ({ ...f, [field]: v }));
    setErrors((prev) => ({
      ...prev,
      placeOfBirthMin: field === "placeOfBirth" ? (v.trim().length > 0 && v.trim().length < 3) : prev.placeOfBirthMin,
      currentResidenceMin: field === "currentResidence" ? (v.trim().length > 0 && v.trim().length < 3) : prev.currentResidenceMin,
      spiritualOrientationMin: field === "spiritualOrientation" ? (v.trim().length > 0 && v.trim().length < 3) : prev.spiritualOrientationMin,
    }));
  };

  const updatePrev =
    (idx: number, key: keyof PrevRow) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => {
        const copy = [...f.previousNames];
        let value = e.target.value;
        if (key === "name") {
          value = alphaOnly(value, 50); // sanitize name
        }
        copy[idx] = { ...copy[idx], [key]: value };
        return { ...f, previousNames: copy };
      });

  const setPrevType = (idx: number, type: "birth" | "specific") =>
    setForm((f) => {
      const copy = [...f.previousNames];
      copy[idx] = { ...copy[idx], type };
      return { ...f, previousNames: copy };
    });

  const setHadOtherNames = (val: "yes" | "no") =>
    setForm((f) => ({
      ...f,
      hadOtherLegalNames: val,
      previousNames:
        val === "no"
          ? []
          : f.previousNames.length
          ? f.previousNames
          : [{ name: "", from: "", to: "", type: "" }],
    }));

  const addPrev = () =>
    setForm((f) => ({
      ...f,
      previousNames:
        f.previousNames.length < 5
          ? [...f.previousNames, { name: "", from: "", to: "", type: "" }]
          : f.previousNames,
    }));

  const removePrev = (idx: number) =>
    setForm((f) => {
      const copy = [...f.previousNames];
      copy.splice(idx, 1);
      return {
        ...f,
        previousNames: copy.length
          ? copy
          : [{ name: "", from: "", to: "", type: "" }],
      };
    });

  // ---------- Validation logic ----------
  // Step 0: name + hadOtherLegalNames + previousNames validation
  const isStep0Valid = (() => {
    if (!form.givenName.trim()) return false;
    if (!form.hadOtherLegalNames) return false;

    if (form.hadOtherLegalNames === "yes") {
      if (!form.previousNames.length) return false;

      return form.previousNames.every((p) => {
        if (!p.name.trim()) return false;
        if (!p.type) return false;
        if (p.type === "specific") {
          if (!p.from || !p.to) return false;
          // enforce from <= to
          if (p.from && p.to && new Date(p.from) > new Date(p.to)) return false;
        }
        return true;
      });
    }
    if(isRelated && !form.relationship){
      return false
    }
    return true;
  })();

  // Step 1: other details
  const isStep1Valid = (() => {
    if (!form.dob || !form.tob) return false;
    if (!form.dominantHand) return false;
    if (!form.placeOfBirth.trim() || form.placeOfBirth.trim().length < 3) return false;
    if (!form.currentResidence.trim() || form.currentResidence.trim().length < 3) return false;

    if (
      form.previousNames.some((p) => p.type === "birth") &&
      (!form.dob || !form.tob)
    ) {
      return false;
    }
    return true;
  })();

  const isFormValid = isStep0Valid && isStep1Valid;

  // ---------- Display helpers ----------
  const fullName = [form.givenName, form.middleNames, form.familyName]
    .filter(Boolean)
    .join(" ");

    const dobDisplay = (() => {
      if (!form.dob || !form.tob) return "—";
    
      const dt = new Date(`${form.dob}T${form.tob}`);
      if (isNaN(dt.getTime())) return "—";
    
      const datePart = dt.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    
      const timePart = dt.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    
      return `${datePart}, ${timePart}`;
    })();
    

  const fmtDate = (d?: string) =>
    d
      ? new Date(`${d}T00:00:00`).toLocaleDateString([], {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "";

  const legalRange = (p: PrevRow) => {
    if (p.type === "birth") return "Since birth";
    if (p.type === "specific") {
      const f = fmtDate(p.from);
      const t = fmtDate(p.to);
      return f && t ? `${f} → ${t}` : "";
    }
    return "";
  };

  function convertTo24Hour(t: string) {
    if (!t) return "";
    // Example: "02:02 AM"
    const [time, modifier] = t.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
  
    if (modifier === "PM" && hours < 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;
  
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  // ---------- Convert to final shape (sanitization at save) ----------
  function convertToFinalProfile(form: ProfileFormState): any {
    const fullNameStr = [form.givenName, form.middleNames, form.familyName]
      .filter(Boolean)
      .join(" ")
      .trim();

    const nicknamesArray: string[] = (() => {
      const v: any = (form as any).nicknames;
      if (!v) return [];
      if (Array.isArray(v)) return v.map((s) => (s ?? "").toString().trim()).filter(Boolean);
      if (typeof v === "string")
        return v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [];
    })();

    // sanitize alpha-only fields for final push (remove any non-alpha chars that might have slipped)
    const sanitizedGiven = stripNonAlpha(form.givenName);
    const sanitizedMiddle = stripNonAlpha(form.middleNames || "");
    const sanitizedFamily = stripNonAlpha(form.familyName || "");

    const sanitizedPrevious = (form.previousNames || [])
      .filter((p) => p.name.trim()) // only include entries with name
      .map((p) => ({
        name: stripNonAlpha(p.name),
        from: p.from || null,
        to: p.to || null,
        type: p.type || "",
      }));

    const traits: any = {
      dominantHand: form.dominantHand || null,
      spiritualOrientation: form.spiritualOrientation?.trim() ? form.spiritualOrientation.trim() : null,
    };

    if (isRelated && form.relationship?.trim()) {
      traits.relationship = form.relationship.trim();
    }

    return {
      birth: {
        date: form.dob || null,
        time: form.tob || null,
        place: form.placeOfBirth?.trim() || null,
      },
      current: {
        residence: form.currentResidence?.trim() || null,
      },
      identity: {
        fullName: fullNameStr || null,
        givenName: sanitizedGiven || null,
        middleNames: sanitizedMiddle || null,
        familyName: sanitizedFamily || null,
        pronoun: form.pronoun || null,
        nicknames: nicknamesArray,
      },
      legalHistory: {
        hadOtherLegalNames: form.hadOtherLegalNames === "yes",
        ...(form.hadOtherLegalNames === "yes"
          ? {
              previousNames: sanitizedPrevious.map((p) => ({
                name: p.name || null,
                from: p.from || null,
                to: p.to || null,
                type: p.type || null,
              })),
            }
          : {}),
      },
      traits,
    };
  }

  // ---------- Navigation and submit ----------
  const handleNext = async () => {
    // simple guard
    if (
      (activeStep === 0 && !isStep0Valid) ||
      (activeStep === 1 && !isStep1Valid)
    )
      return;

    // before moving from step 1, auto-fill previousNames 'birth' from -> dob if needed
    if (activeStep === 1) {
      setForm((f) => ({
        ...f,
        previousNames: f.previousNames.map((p) =>
          p.type === "birth" ? { ...p, from: f.dob, to: "" } : p
        ),
      }));
    }

    if (activeStep === steps.length - 1) {
      if (!isFormValid) return;
      try {
        setLoading(true);
        console.log("herererere")
        const final = convertToFinalProfile(form);
        await onSave(final);
      } finally {
        setLoading(false);
        console.log("herererere-----------")
      }
      return;
    }

    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  // ---------- Render ----------
  return (
    <div className="flex justify-center min-h-screen bg-dark-background px-0 sm:px-8">
      <div className="mx-auto w-full max-w-xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="w-full min-h-[500px] rounded-2xl px-6 py-6 bg-dark-background-secondary">
          {/* header */}
          <div className={
    `!text-dark-text-color mb-6 ` +
    (!isSetupProfile ? "border-b border-dark-border" : "")
  }>
            {!isSetupProfile && (<div onClick={() => setDiscardOpen(true)} className="flex gap-2 items-center mb-2 hover:underline font-medium text-base cursor-pointer">
              <ChevronLeftIcon className="h-4 w-4" />
              <span>Back to profile</span>
            </div>)}
            {!isSetupProfile && (
              <div>
                {isRelated && !initialData && (
                  <div className="font-semibold text-2xl mb-2">Add related profile</div>
                )}
                {isRelated && initialData && (
                  <div className="font-semibold text-2xl mb-2">Edit related profile</div>
                )}
                {!isRelated && <div className="font-semibold text-2xl mb-2">Edit profile</div>}
              </div>
            )}
            
            
            
          </div>

          <div className="sm:px-14">
            {/* Tabs */}
            <div className="mb-6 flex justify-center gap-3">
              {steps.map((step, idx) => (
                <button
                  key={step}
                  onClick={() => setActiveStep(idx)}
                  type="button"
                  className={`px-3 py-1.5 text-sm font-medium transition ${
                    activeStep === idx
                      ? "underline decoration-brand-400 underline-offset-8 text-brand-600 shadow-sm"
                      : "text-dark-text"
                  }`}
                >
                  {step}
                </button>
              ))}
            </div>

            {/* Step 0 */}
            {activeStep === 0 && (
              <section className="space-y-4 flex flex-col gap-4">
                {!isRelated && (<div>
                  <h2 className="text-white font-normal text-xl">Your name</h2>
                  <div className="text-dark-text font-normal text-sm">
                    Your name is one of the most important aspects that help us determine your energetic blueprint. Please provide your full legal name, prior name(s), and nicknames.
                  </div>
                  </div>)}
                {isRelated && (<div>
                  <h2 className="text-white font-normal text-xl">Person's full name</h2>
                  <div className="text-dark-text font-normal text-sm">
                  Person’s name is one of the most important aspects that help us determine the energetic blueprint. Please provide person’s full legal name.
                  </div>
                  </div>)}
                

                  {/* Conditional relationship field when isRelated is true */}
                  {isRelated && (
                      <div>
                        <label className="text-white text-sm font-medium">Relationship <span className="text-red-500">*</span></label>
                        <input
                          className="field-dark w-full"
                          placeholder="Brother, Sister, Friend..."
                          value={form.relationship ?? ""}
                          onChange={(e) => setForm((s) => ({ ...s, relationship: placeSanitize(e.target.value, 100) }))}
                          maxLength={100}
                        />
                      </div>
                    )}
                <div>
                  <div className="text-white text-sm font-medium">
                    First name / Given name <span className="text-red-500">*</span>
                  </div>
                  <input
                    className="field-dark"
                    placeholder="Given name"
                    value={form.givenName}
                    onChange={handleAlphaField("givenName")}
                    maxLength={50}
                  />
                  {isRelated && (<div className="text-dark-text text-sm font-normal">Their first name</div>)}
                  {!isRelated && (<div className="text-dark-text text-sm font-normal">Your first name</div>)}
                </div>

                <div>
                  <div className="text-white text-sm font-medium">Middle name(s)</div>
                  <input
                    className="field-dark"
                    placeholder="Middle names"
                    value={form.middleNames}
                    onChange={handleAlphaField("middleNames")}
                    maxLength={50}
                  />
                  {isRelated && (<div className="text-dark-text text-sm font-normal">Optional, in the order that they use them</div>)}
                  {!isRelated && (<div className="text-dark-text text-sm font-normal">Optional, in the order that you use them</div>)}
                </div>

                <div>
                  <div className="text-white text-sm font-medium">Last Name/ Family name</div>
                  <input
                    className="field-dark"
                    placeholder="Family name"
                    value={form.familyName}
                    onChange={handleAlphaField("familyName")}
                    maxLength={50}
                  />
                  {isRelated && (<div className="text-dark-text text-sm font-normal">Their last name, with suffixes, if any</div>)}
                  {!isRelated && (<div className="text-dark-text text-sm font-normal">Your last name, with suffixes, if any</div>)}
                </div>

                <fieldset>
                {isRelated && (<legend className="text-white text-sm font-medium">
                    Has this person used any other legal names in the past?{" "}
                    <span className="text-red-500">*</span>
                  </legend>)}

                {!isRelated && (<legend className="text-white text-sm font-medium">
                    Have you used any other legal names in the past?{" "}
                    <span className="text-red-500">*</span>
                  </legend>)}

                  <div className="flex gap-6 mt-2 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hadOtherLegalNames"
                        value="yes"
                        checked={form.hadOtherLegalNames === "yes"}
                        onChange={() => setHadOtherNames("yes")}
                        className="peer hidden"
                      />
                      <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                      </span>
                      <span className="text-gray-300 peer-checked:text-white">Yes</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="hadOtherLegalNames"
                        value="no"
                        checked={form.hadOtherLegalNames === "no"}
                        onChange={() => setHadOtherNames("no")}
                        className="peer hidden"
                      />
                      <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                      </span>
                      <span className="text-gray-300 peer-checked:text-white">No</span>
                    </label>
                  </div>
                </fieldset>

                {form.hadOtherLegalNames === "yes" && (
                  <div className="border p-4 rounded border-dark-border space-y-6">
                    {form.previousNames.map((p, idx) => (
                      <div key={idx} className="space-y-3 border-b border-dark-border pb-4">
                        <div>
                          <div className="text-white text-sm font-medium">Full name <span className="text-red-500">*</span></div>
                          <input
                            className="field-dark"
                            placeholder="Full Name"
                            value={p.name}
                            onChange={updatePrev(idx, "name")}
                            maxLength={50}
                          />
                        </div>

                        <div className="flex flex-col gap-2 text-sm">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`prev-type-${idx}`}
                              checked={p.type === "birth"}
                              onChange={() => setPrevType(idx, "birth")}
                              className="peer hidden"
                            />
                            <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                              <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                            </span>
                            <span className="text-gray-300 peer-checked:text-white">Since birth</span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`prev-type-${idx}`}
                              checked={p.type === "specific"}
                              onChange={() => setPrevType(idx, "specific")}
                              className="peer hidden"
                            />
                            <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                              <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                            </span>
                            <span className="text-gray-300 peer-checked:text-white">Since specific date</span>
                          </label>
                        </div>

                        {p.type === "specific" && (
                          <div className="flex gap-3">
                            <input
                              className="field-dark"
                              placeholder="From"
                              type="date"
                              value={p.from}
                              onChange={updatePrev(idx, "from")}
                              max={yesterday}
                            />
                            <input
                              className="field-dark"
                              placeholder="To"
                              type="date"
                              value={p.to}
                              onChange={updatePrev(idx, "to")}
                              min={p.from ? nextDayISO(p.from) : undefined}
                              max={yesterday}
                            />
                          </div>
                        )}

                        <div className="flex gap-3 mt-2">
                          <Button color="darktheme" type="button" onClick={() => removePrev(idx)}>
                            Remove
                          </Button>
                          {form.previousNames.length < 5 && idx === form.previousNames.length - 1 && (
                            <Button color="darktheme" type="button" onClick={addPrev}>
                              Add another
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  {isRelated && (<div className="text-white text-sm font-medium">Their nickname(s)</div>)}
                  {!isRelated && (<div className="text-white text-sm font-medium">Your nickname(s)</div>)}
                  <input className="field-dark" placeholder="Nicknames" value={form.nicknames} onChange={handleNicknamesChange} maxLength={50} />
                  <div className="text-dark-text text-sm font-normal">Separate multiple nicknames with a comma</div>
                </div>

                <div>
                  {isRelated && (<div className="text-white text-sm font-medium">Which pronoun do they prefer?</div>)}
                  {!isRelated && (<div className="text-white text-sm font-medium">Which pronoun do you prefer?</div>)}
                  <select className="field-dark !text-white !pr-2" value={form.pronoun} onChange={updateGeneric("pronoun")}>
                    <option value="">Select...</option>
                    <option value="she/her">She / Her</option>
                    <option value="he/him">He / Him</option>
                    <option value="they/them">They / Them</option>
                    <option value="other">Other</option>
                    <option value="prefer-not">Prefer not to say</option>
                  </select>
                </div>
              </section>
            )}

            {/* Step 1 */}
            {activeStep === 1 && (
              <section className="space-y-6">
                <h2 className="text-xl font-semibold text-white">Other details</h2>
                <p className="text-dark-text text-sm mt-0">Please be as accurate as possible.</p>

                <div>
                  <label className="text-white text-sm font-medium">Date of birth <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    className="field-dark w-full !bg-dark-background-secondary-medium !text-white !placeholder:text-dark-text"
                    value={form.dob}
                    onChange={updateGeneric("dob")}
                    max={yesterday}
                  />
                </div>

                <div>
                  <label className="text-white text-sm font-medium">Time of birth <span className="text-red-500">*</span></label>
                  {/* <input
                    type="time"
                    className="field-dark w-full !bg-dark-background-secondary-medium !text-white !placeholder:text-dark-text"
                    value={form.tob}
                    onChange={updateGeneric("tob")} */}
                  <TimePicker
                    value={form.tob}
                    onChange={(v) => updateGeneric("tob")(convertTo24Hour(v))}
                  />
                  {/* /> */}
                </div>

                <fieldset>
                  <legend className="text-white text-sm font-medium">Dominant hand <span className="text-red-500">*</span></legend>
                  <div className="flex gap-6 mt-2 text-sm">
                    {/* Right */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="dominantHand" value="right" checked={form.dominantHand === "right"} onChange={(e) => updateGeneric("dominantHand")(e)} className="peer hidden" />
                      <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                      </span>
                      <span className="text-gray-300 peer-checked:text-white">Right</span>
                    </label>

                    {/* Left */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="dominantHand" value="left" checked={form.dominantHand === "left"} onChange={(e) => updateGeneric("dominantHand")(e)} className="peer hidden" />
                      <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                      </span>
                      <span className="text-gray-300 peer-checked:text-white">Left</span>
                    </label>

                    {/* Ambidextrous */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="dominantHand" value="ambidextrous" checked={form.dominantHand === "ambidextrous"} onChange={(e) => updateGeneric("dominantHand")(e)} className="peer hidden" />
                      <span className="w-4 h-4 rounded-full border-4 border-gray-400 peer-checked:border-indigo-600 flex items-center justify-center">
                        <span className="w-2 h-2 rounded-full bg-indigo-600 opacity-0 peer-checked:opacity-100"></span>
                      </span>
                      <span className="text-gray-300 peer-checked:text-white">Ambidextrous</span>
                    </label>
                  </div>
                </fieldset>

                <div>
                  <label className="text-white text-sm font-medium">Place of birth <span className="text-red-500">*</span></label>
                  <input className="field-dark w-full" placeholder="" value={form.placeOfBirth} onChange={handlePlaceChange("placeOfBirth")} maxLength={100} />
                  {form.placeOfBirth.trim().length > 0 && form.placeOfBirth.trim().length < 3 && (
                    <div className="text-red-500 text-sm mt-1">Minimum 3 characters required</div>
                  )}
                  <div className="text-dark-text text-sm font-normal">City, State, Country</div>
                </div>

                <div>
                  <label className="text-white text-sm font-medium">Place of current residence <span className="text-red-500">*</span></label>
                  <input className="field-dark w-full" placeholder="" value={form.currentResidence} onChange={handlePlaceChange("currentResidence")} maxLength={100} />
                  {form.currentResidence.trim().length > 0 && form.currentResidence.trim().length < 3 && (
                    <div className="text-red-500 text-sm mt-1">Minimum 3 characters required</div>
                  )}
                  <div className="text-dark-text text-sm font-normal">City, State, Country</div>
                </div>

                <div>
                  {isRelated && (<label className="text-white text-sm font-medium">What is their spiritual orientation?</label>)}
                  {!isRelated && (<label className="text-white text-sm font-medium">What is your spiritual orientation?</label>)}
                  <input className="field-dark w-full" value={form.spiritualOrientation} onChange={handlePlaceChange("spiritualOrientation")} maxLength={100} />
                  {form.spiritualOrientation.trim().length > 0 && form.spiritualOrientation.trim().length < 3 && (
                    <div className="text-red-500 text-sm mt-1">Minimum 3 characters required</div>
                  )}
                </div>

                
              </section>
            )}

            {/* Step 2 */}
            {activeStep === 2 && (
              <section className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-white">Review</h2>
                  <p className="text-sm text-dark-text mt-1">
                    Please review all details carefully. If this information changes, we will have to re-run your reports to preserve their accuracy.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-dark-text text-sm">Full name</p>
                    <p className="text-white">{fullName || "—"}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Nicknames</p>
                    <p className="text-white">{form.nicknames || "—"}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Pronoun</p>
                    <p className="text-white">{form.pronoun || "—"}</p>
                  </div>

                  {form.previousNames.length > 0 && (
                    <div>
                      <p className="text-dark-text text-sm">Previous names</p>
                      <div className="text-white space-y-1">
                        {form.previousNames.map((p, i) => (
                          <div key={i}>
                            {p.name} {legalRange(p)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-dark-text text-sm">Date of birth</p>
                    <p className="text-white">{dobDisplay || "—"}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Dominant hand</p>
                    <p className="text-white capitalize">{form.dominantHand}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Place of birth</p>
                    <p className="text-white">{form.placeOfBirth || "—"}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Current residence</p>
                    <p className="text-white">{form.currentResidence || "—"}</p>
                  </div>

                  <div>
                    <p className="text-dark-text text-sm">Spiritual orientation</p>
                    <p className="text-white">{form.spiritualOrientation || "—"}</p>
                  </div>

                  {/* relationship shown only if isRelated true */}
                  {isRelated && (
                    <div>
                      <p className="text-dark-text text-sm">Relationship</p>
                      <p className="text-white">{form.relationship || "—"}</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Navigation */}
            <div className="mt-6 flex w-full items-center">
              {activeStep === 0 ? (
                <button
                  type="button"
                  className="w-full bg-brand-600 text-white py-2 rounded-lg disabled:opacity-50"
                  disabled={!isStep0Valid || loading}
                  onClick={handleNext}
                >
                  Next
                </button>
              ) : (
                <div className="flex w-full items-center">
                  <button
                    type="button"
                    className="w-[10%] bg-dark-background text-dark-text py-2 rounded-lg disabled:opacity-50"
                    onClick={() => setActiveStep((s) => Math.max(s - 1, 0))}
                  >
                    <ArrowLeftIcon className="h-4 w-4 mx-auto" />
                  </button>
                  <div className="w-[5%]" />
                  <button
                    type="button"
                    className="w-[85%] bg-brand-600 text-white py-2 rounded-lg disabled:opacity-50"
                    disabled={
                      loading ||
                      (activeStep === 1 && !isStep1Valid) ||
                      (activeStep === 2 && !isFormValid)
                    }
                    onClick={handleNext}
                  >
                    {activeStep === steps.length - 1 ? (loading ? "Saving..." : "Confirm") : "Next"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Local CSS for date/time icon color */}
      <style>
        {`
          input[type="date"]::-webkit-calendar-picker-indicator,
          input[type="time"]::-webkit-calendar-picker-indicator {
            filter: invert(1);
            opacity: 0.8;
          }

          input[type="date"]:hover::-webkit-calendar-picker-indicator,
          input[type="time"]:hover::-webkit-calendar-picker-indicator {
            opacity: 1;
            cursor: pointer;
          }
        `}
      </style>
      <DiscardChangesDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          console.log("✅ Changes discarded");
          setDiscardOpen(false);
          navigate(`/user/profile`)
          
        }}
      />
    </div>
  );
}
